import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import MobileAppBar from '@/components/molecules/MobileAppBar';
import TabPills from '@/components/molecules/TabPills';
import Pagination from '@/components/molecules/Pagination';
import SearchInputField from '@/components/molecules/SearchInputField';
import WorkActivityTable from '@/components/molecules/workActivity/WorkActivityTable';
import { Button } from '@/components/ui/button';
import { budgetService } from '@/services/budget.service';
import { workActivityService } from '@/services/work-activity.service';
import { WorkActivity, WorkActivityStatus } from '@/services/types/work-activity.types';
import { PAGE } from '@/routes';

import searchIcon from '@/assets/icons/search.svg';
import workActivityIcon from '@/assets/icons/work-activity.svg';
import noWorkActivityIcon from '@/assets/icons/no-work-activity.svg';

const FILTER_OPTIONS = ['All', 'Approved', 'Pending', 'Rejected'] as const;

const STATUS_BY_FILTER: Record<(typeof FILTER_OPTIONS)[number], WorkActivityStatus | undefined> = {
  All: undefined,
  Approved: 'APPROVED',
  Pending: 'PENDING',
  Rejected: 'REJECTED'
};

const WorkActivityPage = () => {
  const navigate = useNavigate();
  const { budgetId } = useParams<{ budgetId: string }>();

  const [activeTab, setActiveTab] = useState<(typeof FILTER_OPTIONS)[number]>('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [workActivities, setWorkActivities] = useState<WorkActivity[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState('EUR');

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!budgetId) {
      return;
    }

    budgetService
      .getBudget(budgetId)
      .then(({ data }) => {
        setCurrency(data.currency ?? 'EUR');
      })
      .catch((error) => {
        console.error('Error fetching budget:', error);
      });
  }, [budgetId]);

  useEffect(() => {
    if (!budgetId) {
      return;
    }

    workActivityService
      .getWorkActivities(budgetId, {
        status: 'PENDING',
        type: 'all',
        page: 1,
        limit: 1
      })
      .then((response) => {
        setPendingCount(response.totalCount ?? 0);
      })
      .catch((error) => {
        console.error('Error fetching pending work activity count:', error);
      });
  }, [budgetId]);

  useEffect(() => {
    if (!budgetId) {
      return;
    }

    const status = STATUS_BY_FILTER[activeTab];

    setLoading(true);
    workActivityService
      .getWorkActivities(budgetId, {
        status,
        type: 'all',
        search: debouncedSearch || undefined,
        page: currentPage,
        limit: perPage
      })
      .then((response) => {
        setWorkActivities(response.data ?? []);
        setTotalPages(response.totalPages || 1);
        setTotalCount(response.totalCount ?? 0);
        setCurrentPage(response.currentPage || 1);
      })
      .catch((error) => {
        console.error('Error fetching work activities:', error);
        toast.error(error?.message ?? 'Failed to load work activities.');
        setWorkActivities([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [budgetId, activeTab, debouncedSearch, currentPage, perPage]);

  const tabBadges = useMemo(() => (pendingCount > 0 ? { Pending: pendingCount } : undefined), [pendingCount]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (nextPerPage: number) => {
    setPerPage(nextPerPage);
    setCurrentPage(1);
  };

  const handleWorkActivitySelect = (workActivity: WorkActivity) => {
    if (budgetId) {
      navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity/${workActivity._id}`);
    }
  };

  const handleCreateWorkActivity = () => {
    if (budgetId) {
      navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity/create`, {
        state: { from: 'budget', budgetId }
      });
    }
  };

  const hasFilters = activeTab !== 'All' || !!debouncedSearch;
  const showEmptyState = !loading && workActivities.length === 0 && !hasFilters;
  const showTable = loading || workActivities.length > 0 || hasFilters;

  return (
    <>
      <MobileAppBar title="Work Activity" variant="primary" onRightIconClick={() => {}} rightIcon={searchIcon} />

      <div className="flex-1 flex w-full max-w-[1440px] mx-auto overflow-hidden">
        <div className="flex-1 flex flex-col gap-4">
          <div className="flex-1 bg-white w-full md:rounded-[16px] p-0 pt-1 md:p-4 border flex flex-col gap-4">
            <header className="hidden md:flex items-center justify-between gap-4">
              <h2 className="flex items-center gap-2 text-2xl font-semibold shrink-0">
                <img src={workActivityIcon} alt="work activity" className="w-6 h-6" />
                <span>Work Activity</span>
              </h2>
              <SearchInputField
                searchIcon={searchIcon}
                onSearch={setSearchTerm}
                placeholder="Search for activity name"
                className="max-w-[280px]"
              />
            </header>

            <div className="px-4 md:hidden">
              <SearchInputField
                searchIcon={searchIcon}
                onSearch={setSearchTerm}
                placeholder="Search for activity name"
              />
            </div>

            <div className="flex items-center justify-between gap-3 px-4 md:px-0">
              <TabPills
                options={[...FILTER_OPTIONS]}
                value={activeTab}
                onValueChange={(value) => {
                  setActiveTab(value as (typeof FILTER_OPTIONS)[number]);
                  setCurrentPage(1);
                }}
                badges={tabBadges}
              />
            </div>

            <div className="flex-1 flex overflow-y-auto max-h-[calc(100vh-270px)] md:max-h-none">
              {showTable ? (
                <div className="flex-1 flex px-4 md:px-0 mt-2 md:mt-0">
                  <WorkActivityTable
                    workActivities={workActivities}
                    loading={loading}
                    currency={currency}
                    budgetId={budgetId}
                    onWorkActivitySelect={handleWorkActivitySelect}
                  />
                </div>
              ) : showEmptyState ? (
                <div className="flex-1 flex items-center justify-center flex-col gap-4 px-4">
                  <div className="flex flex-col items-center gap-2">
                    <img className="w-[80px] h-[80px]" src={noWorkActivityIcon} alt="no work activity icon" />
                    <div className="flex flex-col items-center justify-center text-center">
                      <p className="font-medium leading-[140%]">There are no work activity to show.</p>
                      <span className="text-secondary text-sm leading-[125%]">
                        You can create work activity to request a payment
                      </span>
                    </div>
                  </div>
                  <Button onClick={handleCreateWorkActivity} variant="primary-reverse" className="rounded-full px-6">
                    Create a Work Activity
                  </Button>
                </div>
              ) : null}
            </div>
          </div>

          <div className="hidden md:block">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              loading={loading}
              perPage={perPage}
              onPerPageChange={handlePerPageChange}
              totalCount={totalCount}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default WorkActivityPage;
